
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

// Define the authentication context types
type AuthContextType = {
  session: Session | null;
  user: User | null;
  profile: any | null;
  userRoles: string[];
  isLoading: boolean;
  isAuthenticated: () => boolean;
  isAdmin: () => boolean;
  isEditor: () => boolean;
  signIn: (email: string, password: string) => Promise<{ error: any | null }>;
  signUp: (email: string, password: string, metadata: { first_name: string; last_name: string }) => Promise<{ error: any | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Fetch user profile data when user changes
  useEffect(() => {
    if (user) {
      fetchProfile();
      fetchUserRoles();
    } else {
      setProfile(null);
      setUserRoles([]);
    }
  }, [user]);

  // Function to fetch user profile with improved error handling
  const fetchProfile = async () => {
    if (!user) return;
    
    try {
      console.log("Fetching profile for user ID:", user.id);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
        
      if (error) {
        console.error('Error fetching profile:', error);
        
        // If profile doesn't exist, the trigger should have created it
        // Let's try to create it manually as a fallback
        if (error.code === 'PGRST116') { // No rows returned
          console.log('Profile not found, creating one...');
          const { data: newProfile, error: createError } = await supabase
            .from('profiles')
            .insert({
              id: user.id,
              first_name: user.user_metadata?.first_name || '',
              last_name: user.user_metadata?.last_name || '',
              email: user.email || '',
              role: 'user',
              status: 'active'
            })
            .select()
            .single();
            
          if (createError) {
            console.error('Error creating profile:', createError);
          } else {
            console.log('Profile created successfully:', newProfile);
            setProfile(newProfile);
          }
        }
        return;
      }
      
      if (data) {
        console.log('Profile data loaded:', data);
        setProfile(data);
      } else {
        console.warn('No profile found for user:', user.id);
      }
    } catch (err) {
      console.error('Error in fetchProfile:', err);
    }
  };

  // Function to fetch user roles from user_roles table
  const fetchUserRoles = async () => {
    if (!user) return;
    
    try {
      console.log("Fetching user roles for user ID:", user.id);
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);
        
      if (error) {
        console.error('Error fetching user roles:', error);
        setUserRoles([]);
        return;
      }
      
      if (data) {
        const roles = data.map(r => r.role);
        console.log('User roles loaded:', roles);
        setUserRoles(roles);
      } else {
        console.warn('No roles found for user:', user.id);
        setUserRoles([]);
      }
    } catch (err) {
      console.error('Error in fetchUserRoles:', err);
      setUserRoles([]);
    }
  };

  // Function to refresh the user profile data
  const refreshProfile = async () => {
    await fetchProfile();
    await fetchUserRoles();
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!user;
  };

  // Check if user is admin - uses user_roles table exclusively
  const isAdmin = () => {
    console.log('Checking admin status:', { userRoles, userEmail: user?.email });
    return userRoles.includes('admin');
  };

  // Check if user is editor - uses user_roles table exclusively
  const isEditor = () => {
    console.log('Checking editor status:', { userRoles, userEmail: user?.email });
    return userRoles.includes('editor') || userRoles.includes('admin');
  };

  // Sign in function
  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  // Sign up function
  const signUp = async (email: string, password: string, metadata: { first_name: string; last_name: string }) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata
      }
    });
    return { error };
  };

  // Sign out function
  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const value = {
    session,
    user,
    profile,
    userRoles,
    isLoading,
    isAuthenticated,
    isAdmin,
    isEditor,
    signIn,
    signUp,
    signOut,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
